import {
    managerIdParameterName,
    workflowStatusParameterName,
    budgetCycleIdParameterName,
    reportsParameterName,
    employeeSourceIdParameterName,
    sidePanelParamaterName,
    viewParameterName,
    workflowViewParamater,
    currentBudgetParameter,
    rulesPanelParamater,
    rulesTabParamater,
    employeeSortParamaterName,
    jobFilterParamaterName,
    departmentFilterParamaterName,
    employeeFilterParamaterName,
    promotionFilterParamaterName
} from 'nomst/Stores/Navigation/Constants/decisionPageUrl';
import { Views, WorkflowStatusListType, ReportsView, TeamRoutes, ConfigTabs } from 'typings/enums';
import { ICycleNavigation } from 'typings/cycleNavigation';
import { IEmployeeViewNavigationParameters } from 'typings/navigation';

export const getOrgChartRoute = (managerSourceId: string): string => {
    // tslint:disable-next-line:max-line-length
    return `${TeamRoutes.Decision}/${Views.OrgChart}?${managerIdParameterName}=${encodeURIComponent(managerSourceId)}`;
};

interface IUrlParams {
    sidePanel: string,
    employeeSort: string,
    promotionFilter: string,
    jobFilter: string,
    departmentFilter: string,
    employeeFilter: string
}

const getUrlParams = (navParams: IEmployeeViewNavigationParameters): IUrlParams => {
    const sidePanel = `${sidePanelParamaterName}=${navParams.ShowSidePanel}`;
    const employeeSort = `${employeeSortParamaterName}=${encodeURIComponent(navParams.SortBy ?? '')}`;
    const promotionFilter = `&${promotionFilterParamaterName}=${!!navParams.PromotionFilter}`;

    // optional params
    const jobFilter = navParams.JobTitleFilter ? `&${jobFilterParamaterName}=${encodeURIComponent(navParams.JobTitleFilter)}` : '';
    const departmentFilter = navParams.DepartmentFilter ? `&${departmentFilterParamaterName}=${encodeURIComponent(navParams.DepartmentFilter)}` : '';
    const employeeFilter = navParams.SelectedEmployee ? `&${employeeFilterParamaterName}=${encodeURIComponent(navParams.SelectedEmployee)}` : '';
    return {
        sidePanel,
        employeeSort,
        promotionFilter,
        jobFilter,
        departmentFilter,
        employeeFilter
    };
};

export const getWorkflowManagersRoute = (navParams: IEmployeeViewNavigationParameters): string => {
    let ids = '';
    if (navParams.ManagerSourceIds?.length) {
        ids = navParams.ManagerSourceIds.map(id =>
            `${managerIdParameterName}=${encodeURIComponent(id)}`).join('&');
        ids += '&';
    }
    const {        
        sidePanel,
        employeeSort,
        promotionFilter,
        jobFilter,
        departmentFilter,
        employeeFilter

    } = getUrlParams(navParams);

    // tslint:disable-next-line:max-line-length
    return `${TeamRoutes.Decision}?cycleId=${navParams.CycleId}&${ids}${workflowStatusParameterName}=${navParams.WorkflowStatus}&${reportsParameterName}=${navParams.ReportsView}&${sidePanel}&${employeeSort}${promotionFilter}${jobFilter}${departmentFilter}${employeeFilter}`;
};

export const getCycleSettingsRoute = (
    cycleId: number
): string => {
    return `${TeamRoutes.CycleSettings}?cycleId=${cycleId}`;
};

// navigate out of cycle settings to workflowSettings
export const getMstWorkflowSettingsRoute = (cycleId: number): string => {
    return `${TeamRoutes.MstCycleConfig}?cycleId=${cycleId}&view=workflow&workflowType=hrroles`;
};

// navigate out of cycle settings to budtgetSettings
export const getMstBudgetSettingsRoute = (cycleId: number): string => {
    return `${TeamRoutes.MstCycleConfig}?cycleId=${cycleId}&view=budget`;
};

export const getEmptyManagerViewRoute = (): string => {
    return `${TeamRoutes.CycleNotOpenManagerView}`;
};

export const getErrorMessageViewRoute = (): string => {
    return `${TeamRoutes.AccountError}`;
};

export const getDiagnosticsRoute = (): string => {
    return `${TeamRoutes.Diagnostics}`;
};

export const getSelectedEmployeeDetailsRoute = (
    navParams: IEmployeeViewNavigationParameters,
    selectedEmployee: NullOrUndefined<string>): string => {

    if (navParams.ManagerSourceIds?.length) {
        let url = getWorkflowManagersRoute(navParams);
        if (selectedEmployee) {
            url = url + `&${employeeSourceIdParameterName}=${encodeURIComponent(selectedEmployee)}`;
        }
        return url;
    }
    return '';
};

export const getOverBudgetManagersRoute = (navParams: IEmployeeViewNavigationParameters, budgetCycleId: number): string => {
    const ids = navParams.ManagerSourceIds ? navParams.ManagerSourceIds.map(id =>
        `${managerIdParameterName}=${encodeURIComponent(id)}`).join('&') : '';

    const {        
        sidePanel,
        employeeSort,
        promotionFilter,
        jobFilter,
        departmentFilter,
        employeeFilter

    } = getUrlParams(navParams);

    // tslint:disable-next-line:max-line-length
    return `${TeamRoutes.Decision}?cycleId=${navParams.CycleId}&${ids}&${budgetCycleIdParameterName}=${budgetCycleId}&${reportsParameterName}=${navParams.ReportsView}&${sidePanel}&${employeeSort}${promotionFilter}${jobFilter}${departmentFilter}${employeeFilter}`;
};

export const getChangeHistoryRoute =    (
    cycleId: number,
    managerSourceId: string,
    workflowStatusListType: WorkflowStatusListType,
    reports: ReportsView = ReportsView.DirectsOnly): string => {

    // tslint:disable-next-line:max-line-length
    return `${TeamRoutes.ChangeHistory}?cycleId=${cycleId}&${managerIdParameterName}=${encodeURIComponent(managerSourceId)}&${workflowStatusParameterName}=${workflowStatusListType}&${reportsParameterName}=${reports}`;
};

export const getAccessDeniedRoute = () => {
    return TeamRoutes.AccessDenied;
};

export const getConfigurationRoute = (
    configTab: ConfigTabs) => {
    const configViewQuery = `${viewParameterName}=${configTab}`;
    return `${TeamRoutes.GlobalConfiguration}?${configViewQuery}`;
};

export const getCycleConfigurationRoute = (nav: ICycleNavigation) => {
    const cycleIdvar = `cycleId=${nav.cycleId}`;
    const workflowType = nav.workflowView ? `&${workflowViewParamater}=${nav.workflowView}` : '';
    const budgetType = nav.amountCode ? `&${currentBudgetParameter}=${nav.amountCode}` : '';
    const rulesPanelView = nav.rulesPanel ? `&${rulesPanelParamater}=${nav.rulesPanel}` : '';
    const viewPart = nav.cycleId > 0 ? `&view=${nav.view}` : '';
    const rulesTabParam = nav.rulesPanel ? `&${rulesTabParamater}=${nav.rulesTab}` : '';
    const cycleUrlVariables =
        `${cycleIdvar}${viewPart}${budgetType}${workflowType}${rulesPanelView}${rulesTabParam}`;
    return `${TeamRoutes.CycleConfiguration}?${cycleUrlVariables}`;
};